import {
  LEVEL_ONE_PATH,
  LEVEL_ONE_PLACEMENT_TILES,
} from "../constants/constants";
import {
  calculateDistance,
  getScaleToResolution,
  scalePositionToResolution,
} from "../helpers/mathFunctions";
import Position from "../types/PositionType";
import { Enemy } from "./Enemy";
import { GameStats } from "./GameStats";
import { PlacementTiles } from "./PlacementTiles";
import { Tower } from "./Tower";

const gazableDiv =
  document.getElementById("gazable-components") ||
  document.createElement("div");

export class Canvas {
  private canvas: HTMLCanvasElement;
  private context: CanvasRenderingContext2D;
  private _activePlacementTile: PlacementTiles = new PlacementTiles(
    { x: 0, y: 0 },
    this
  );
  private _gameStats: GameStats = new GameStats(this);
  private _mouse: Position = { x: 0, y: 0 };
  private _enemies: Enemy[] = [];
  private _towers: Tower[] = [];
  private _placementTiles: PlacementTiles[] = [];
  private _paused: boolean = true;
  private _gazeModeOn: boolean = true;
  private _enemyNumber: number = 5;
  private _backgroundImage: HTMLImageElement = new Image();
  private _dx: number = 0;
  private _dy: number = 0;
  private _waveTimer: number = 20;
  private _wavePreparation: boolean = false;
  private _isArcherTower: boolean = true;
  private _canvasScale: number = 1;

  constructor(canvas: HTMLCanvasElement, context: CanvasRenderingContext2D) {
    this.canvas = canvas;
    this.context = context;
    this.gameInit();
    this._canvasScale = getScaleToResolution(this.canvas.width);
  }

  public gameInit(): void {
    this.initVariables();
    this.initEnemies();
    this.initPlacementTiles();
  }

  private initVariables(): void {
    this.activePlacementTile = new PlacementTiles({ x: 0, y: 0 }, this);
    this._paused = true;
    this._gameStats = new GameStats(this);
    this._enemies = [];
    this._placementTiles = [];
    this._towers = [];
    this._mouse = { x: 0, y: 0 };
    this._enemyNumber = 5;
  }

  public handleCanvasClick(): void {
    const { activePlacementTile, gameStats } = this;
    if (
      !activePlacementTile.isOccupied &&
      activePlacementTile.position.y !== 0 &&
      gameStats.moneyLeft >= (this.isArcherTower ? 50 : 80)
    ) {
      this.placeTower();
    }
  }

  private placeTower(): void {
    const { activePlacementTile, gameStats } = this;
    this._towers.push(
      new Tower(
        activePlacementTile.position,
        this,
        this.isArcherTower,
        this._canvasScale
      )
    );
    activePlacementTile.isOccupied = true;
    gameStats.moneyLeft -= this.isArcherTower ? 50 : 80;
    const moneyLeftDiv = document.getElementById("money-count");
    if (moneyLeftDiv) {
      moneyLeftDiv.innerText = gameStats.moneyLeft.toString();
    }
  }

  public restartGame(): void {
    this.wavePreparation = false;
    this.gameInit();
    this.resetStats();
  }

  private getHoveredPlacementTile(
    activeX: number,
    activeY: number,
    scale: number
  ): PlacementTiles {
    for (const tile of this.placementTiles) {
      const { position, size } = tile;
      const { x, y } = position;
      if (
        activeX >= x &&
        activeX <= x + size &&
        activeY >= y &&
        activeY <= y + size
      ) {
        return tile;
      }
    }
    return new PlacementTiles({ x: 0, y: 0 }, this);
  }

  private initEnemies(numberOfEnemies: number = 5): void {
    for (let i = 0; i < numberOfEnemies; i++) {
      const offsetX = 40 * i;
      this._enemies.push(
        new Enemy(this, {
          ...scalePositionToResolution(LEVEL_ONE_PATH[0], this.canvas.width),
          x:
            scalePositionToResolution(LEVEL_ONE_PATH[0], this.canvas.width).x -
            offsetX,
        })
      );
    }
  }

  private initPlacementTiles(): void {
    LEVEL_ONE_PLACEMENT_TILES.forEach((row, rowIndex) => {
      row.forEach((column, columnIndex) => {
        if (column !== 0) {
          this.createPlacementTile(rowIndex, columnIndex);
        }
      });
    });
  }

  private createPlacementTile(rowIndex: number, columnIndex: number): void {
    this.placementTiles.push(
      new PlacementTiles(
        {
          x: columnIndex * 32 * getScaleToResolution(this.width),
          y: rowIndex * 32 * getScaleToResolution(this.width),
        },
        this
      )
    );
  }

  public handlePlacementTowerClick = (
    x: number,
    y: number,
    scale: number,
    towerType: string
  ) => {
    this.activePlacementTile = this.getHoveredPlacementTile(x, y, scale);
    this.isArcherTower = towerType === "archer";
    this.handleCanvasClick();
  };

  public drawRect(
    x: number,
    y: number,
    width: number,
    height: number,
    color: string
  ): void {
    this.context.fillStyle = color;
    this.context.fillRect(x, y, width, height);
  }

  public drawCircle(
    x: number,
    y: number,
    diameter: number,
    color: string
  ): void {
    this.context.beginPath();
    this.context.arc(x, y, diameter, 0, Math.PI * 2);
    this.context.fillStyle = color;
    this.context.fill();
  }

  public drawImage(
    image: HTMLImageElement,
    cropSpriteX: number,
    cropSpriteY: number,
    cropSpriteWidth: number,
    cropSpriteHeight: number,
    x: number,
    y: number,
    width: number,
    height: number
  ) {
    this.context.drawImage(
      image,
      cropSpriteX,
      cropSpriteY,
      cropSpriteWidth,
      cropSpriteHeight,
      x,
      y,
      width,
      height
    );
  }

  public drawBackground(image: HTMLImageElement, dx: number, dy: number): void {
    this.backgroundImage = image;
    this.dx = dx;
    this.dy = dy;
    this.clearCanvas(dx, dy, image.width, image.height);
    image.onload = () => {
      this.context.drawImage(
        image,
        dx,
        dy,
        this.canvas.width,
        this.canvas.height
      );
      this.animateEnemy(image, dx, dy);
    };
  }

  public reDrawBackground(
    image: HTMLImageElement,
    dx: number,
    dy: number
  ): void {
    this.clearCanvas(dx, dy, image.width, image.height);
    this.context.drawImage(
      image,
      dx,
      dy,
      this.canvas.width,
      this.canvas.height
    );
    this.gameInit();
  }

  public animateEnemy(image: HTMLImageElement, dx: number, dy: number): void {
    const animationId = requestAnimationFrame(() =>
      this.animateEnemy(image, dx, dy)
    );
    this.context.drawImage(
      image,
      dx,
      dy,
      this.canvas.width,
      this.canvas.height
    );

    // Shooting and tower placement
    this.towers.forEach((tower) => {
      tower.update();
      if (!this.paused) {
        const validEnemies = this.enemies.filter((enemy) => {
          const distance = calculateDistance(
            enemy.center,
            tower.center.x,
            tower.center.y
          );

          return distance < enemy.width / 2 + tower.towerRange;
        });
        tower.target = validEnemies[0];
        for (let index = tower.projectiles.length - 1; index >= 0; index--) {
          const projectile = tower.projectiles[index];

          projectile.update();

          const { x, y } = projectile.position;

          const enemyHit = this.enemies.find((enemy) => {
            const distance = calculateDistance(enemy.center, x, y);
            return distance < enemy.width / 2 + projectile.size;
          });
          if (enemyHit) {
            enemyHit.health -= tower.isArcherTower ? 20 : 40;
            if (enemyHit.health <= 0) {
              this.enemies.splice(this.enemies.indexOf(enemyHit), 1);
              this.gameStats.moneyLeft += 10;

              const moneyLeftDiv = document.getElementById("money-count");
              if (moneyLeftDiv) {
                moneyLeftDiv.innerText = this.gameStats.moneyLeft.toString();
              }
              if (this.enemies.length === 0) {
                this.wavePreparation = true;
              }
            }
            tower.projectiles.splice(index, 1);
          }
        }
      }
    });

    // Drawing enemies on the canvas
    for (let i = 0; i < this.enemies.length; i++) {
      const enemy = this.enemies[i];

      enemy.updatePosition();
      if (enemy.position.x > this.canvas.width || enemy.position.y < -20) {
        const heartsCountDisplay = document.getElementById("hearts-count");
        this.enemies.splice(i, 1);
        this.gameStats.livesLeft -= 1;
        if (heartsCountDisplay) {
          heartsCountDisplay.innerText = this.gameStats.livesLeft.toString();
        }
        if (this.gameStats.livesLeft === 0) {
          const gameOverDiv = document.getElementById("game-over-screen");
          cancelAnimationFrame(animationId);
          if (gameOverDiv) {
            gameOverDiv.style.display = "flex";
          }
        }
      }

      if (this.enemies.length === 0) {
        this.wavePreparation = true;
      }
    }

    if (this.enemies.length === 0 && this.wavePreparation) {
      this.wavePreparation = false;

      this._waveTimer = 20;
      var x = setInterval(() => {
        var element = document.getElementById("wave-countdown--time");
        if (element) {
          element.innerHTML = this._waveTimer + "";
        }

        if (this._waveTimer === 0) {
          clearInterval(x);
          this.enemyNumber += 5;
          this.initEnemies(this.enemyNumber);
        }
        if (!this.paused) {
          this._waveTimer -= 1;
        }
      }, 1000);
    }
  }

  private resetStats(): void {
    var element = document.getElementById("wave-countdown--time");
    if (element) {
      element.innerHTML = "0";
    }
    var moneyLeftDiv = document.getElementById("money-count");
    if (moneyLeftDiv) {
      moneyLeftDiv.innerText = "500";
    }
    var heartsCountDisplay = document.getElementById("hearts-count");
    if (heartsCountDisplay) {
      heartsCountDisplay.innerText = "10";
    }
  }

  public getCanvasPlacement() {
    const { top, left } = this.canvas.getBoundingClientRect();
    return { top, left };
  }

  public clearCanvas(dx: number, dy: number, width: number, height: number) {
    this.context.clearRect(dx, dy, width, height);
  }

  public get width(): number {
    return this.canvas.width;
  }

  public set width(width: number) {
    this.canvas.width = width;
  }

  public get height(): number {
    return this.canvas.height;
  }

  public set height(height: number) {
    this.canvas.height = height;
  }

  public get mouse(): Position {
    return this._mouse;
  }

  public set mouse(mouse: Position) {
    this._mouse = mouse;
  }

  public get gameStats(): GameStats {
    return this._gameStats;
  }

  public set gameStats(gameStats: GameStats) {
    this._gameStats = gameStats;
  }

  public get enemies(): Enemy[] {
    return this._enemies;
  }

  public get towers(): Tower[] {
    return this._towers;
  }

  public get placementTiles(): PlacementTiles[] {
    return this._placementTiles;
  }

  public get paused(): boolean {
    return this._paused;
  }

  public set paused(paused: boolean) {
    this._paused = paused;
  }

  public get gazeMode(): boolean {
    return this._gazeModeOn;
  }

  public set gazeMode(gazeMode: boolean) {
    this._gazeModeOn = gazeMode;
  }

  public get enemyNumber(): number {
    return this._enemyNumber;
  }

  public set enemyNumber(enemyNumber: number) {
    this._enemyNumber = enemyNumber;
  }

  private get backgroundImage(): HTMLImageElement {
    return this._backgroundImage;
  }

  private set backgroundImage(backgroundImage: HTMLImageElement) {
    this._backgroundImage = backgroundImage;
  }

  public get dx(): number {
    return this._dx;
  }

  public set dx(dx: number) {
    this._dx = dx;
  }

  public get dy(): number {
    return this._dy;
  }

  public set dy(dy: number) {
    this._dy = dy;
  }

  public get wavePreparation(): boolean {
    return this._wavePreparation;
  }

  public set wavePreparation(wavePreparation: boolean) {
    this._wavePreparation = wavePreparation;
  }

  public get isArcherTower(): boolean {
    return this._isArcherTower;
  }

  public set isArcherTower(isArcherTower: boolean) {
    this._isArcherTower = isArcherTower;
  }

  public set activePlacementTile(activePlacementTile: PlacementTiles) {
    this._activePlacementTile = activePlacementTile;
  }

  public get activePlacementTile(): PlacementTiles {
    return this._activePlacementTile;
  }
}
